import React, { useState, useEffect } from "react";
import { Upload, Button, Modal, Image, message, Spin } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  CloudUploadOutlined,
} from "@ant-design/icons";
import axios from "axios";

interface ProductImageGalleryProps {
  productId: number;
  readOnly?: boolean;
  /**
   * Nombre maximum d'images affichables/uploadables côté UI.
   * Défaut 10 (produits classiques). Les déclinaisons utilisent 5.
   * Note : le backend conserve sa propre limite de 10 ; ce plafond est une garde UI.
   */
  maxImages?: number;
}

const ProductImageGallery: React.FC<ProductImageGalleryProps> = ({
  productId,
  readOnly = false,
  maxImages = 10,
}) => {
  const [gallery, setGallery] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [uploading, setUploading] = useState(false);

  // Récupérer galerie au montage
  useEffect(() => {
    fetchGallery();
  }, [productId]);

  const fetchGallery = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/products/${productId}/gallery`);
      setGallery(response.data.gallery || []);
    } catch (error: any) {
      console.error("Erreur chargement galerie:", error);
      message.error("Erreur chargement des images");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file: File) => {
    // ✅ Validation taille fichier AVANT upload (limite backend: 10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_FILE_SIZE) {
      message.error(
        `L'image est trop grande (${(file.size / 1024 / 1024).toFixed(
          2
        )} MB). Taille maximale: 10 MB`
      );
      return false;
    }

    // ✅ Validation type fichier
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      message.error("Format non supporté. Formats autorisés: JPEG, PNG, WebP");
      return false;
    }

    const formData = new FormData();
    formData.append("images", file);

    const token = localStorage.getItem("token");

    setUploading(true);
    try {
      const response = await axios.post(
        `/api/products/${productId}/gallery`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          timeout: 120000, // 2 minutes pour grandes images
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );
          },
        }
      );

      setGallery(response.data.gallery);
      message.success(response.data.message);
    } catch (error: any) {
      console.error("Erreur upload:", error);
      message.error(error.response?.data?.error || "Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }

    return false; // Empêcher upload automatique
  };

  const handleDelete = async (index: number) => {
    Modal.confirm({
      title: "Supprimer cette image ?",
      content: "Cette action est irréversible",
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          const response = await axios.delete(
            `/api/products/${productId}/gallery/${index}`
          );
          setGallery(response.data.gallery);
          message.success("Image supprimée");
        } catch (error: any) {
          console.error("Erreur suppression:", error);
          message.error("Erreur lors de la suppression");
        }
      },
    });
  };

  const handlePreview = (imageUrl: string) => {
    setPreviewImage(imageUrl);
    setPreviewVisible(true);
  };

  /**
   * ⚡ Convertir URL JPEG en WebP (convention de nommage)
   * Exemple: image.jpg -> image.webp
   */
  const getWebPUrl = (jpegUrl: string): string | undefined => {
    if (!jpegUrl) return undefined;

    // Vérifier que l'URL se termine par .jpg/.jpeg
    if (!/\.jpe?g$/i.test(jpegUrl)) {
      return undefined; // Pas une image JPEG valide
    }

    return jpegUrl.replace(/\.jpe?g$/i, ".webp");
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <Spin tip="Chargement de la galerie..." />
      </div>
    );
  }

  return (
    <div className="product-image-gallery">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        {gallery.map((imageUrl, index) => {
          const webpUrl = getWebPUrl(imageUrl);

          return (
            <div
              key={index}
              style={{
                position: "relative",
                aspectRatio: "1",
                border: "1px solid #d9d9d9",
                borderRadius: "8px",
                overflow: "hidden",
                background: "#fafafa",
              }}
            >
              {/* ⚡ Picture avec WebP + JPEG fallback */}
              <picture>
                {webpUrl && <source srcSet={webpUrl} type="image/webp" />}
                <img
                  src={imageUrl}
                  alt={`Produit ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  onError={(e) => {
                    console.error(
                      `❌ [IMG ${index + 1}] Erreur chargement:`,
                      imageUrl
                    );
                    e.currentTarget.style.background = "#ffebee";
                  }}
                />
              </picture>
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(0,0,0,0.5)",
                  opacity: 0,
                  transition: "opacity 0.3s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
                className="image-overlay"
              >
                <Button
                  type="primary"
                  shape="circle"
                  icon={<EyeOutlined />}
                  onClick={() => handlePreview(imageUrl)}
                />
                {!readOnly && (
                  <Button
                    type="primary"
                    danger
                    shape="circle"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(index)}
                  />
                )}
              </div>
            </div>
          );
        })}

        {/* Upload button */}
        {!readOnly && gallery.length < maxImages && (
          <Upload
            beforeUpload={handleUpload}
            showUploadList={false}
            accept="image/*"
            disabled={uploading}
          >
            <div
              style={{
                aspectRatio: "1",
                border: "2px dashed #d9d9d9",
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: uploading ? "not-allowed" : "pointer",
                background: "#fafafa",
                transition: "all 0.3s",
              }}
              className="upload-placeholder"
            >
              {uploading ? (
                <Spin />
              ) : (
                <>
                  <CloudUploadOutlined
                    style={{ fontSize: "32px", color: "#999" }}
                  />
                  <div
                    style={{
                      marginTop: "8px",
                      fontSize: "12px",
                      color: "#999",
                    }}
                  >
                    Ajouter
                  </div>
                </>
              )}
            </div>
          </Upload>
        )}
      </div>

      {gallery.length === 0 && !uploading && (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "#999",
            background: "#fafafa",
            borderRadius: "8px",
          }}
        >
          Aucune image dans la galerie
          {!readOnly && ". Cliquez sur + pour ajouter des images."}
        </div>
      )}

      {gallery.length > 0 && (
        <div style={{ marginTop: "8px", fontSize: "12px", color: "#999" }}>
          {gallery.length} / {maxImages} images
        </div>
      )}

      {/* Preview Modal */}
      <Modal
        open={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        centered
        width="auto"
        style={{ maxWidth: "90vw" }}
      >
        <Image
          src={previewImage}
          alt="Preview"
          preview={false}
          style={{ maxWidth: "100%", maxHeight: "80vh" }}
        />
      </Modal>

      <style>{`
        .image-overlay:hover {
          opacity: 1 !important;
        }
        .upload-placeholder:hover {
          border-color: #1890ff !important;
          background: #f0f8ff !important;
        }
      `}</style>
    </div>
  );
};

export default ProductImageGallery;
